import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { SiteLayout } from "../components/site/SiteLayout";
import { SEO } from "../components/site/SEO";

const STEPS = [
  {
    index: "01",
    title: "Conversation",
    body:
      "Start with a straightforward chat. What are you working on? What's not quite right? What have you already tried? No jargon, no sales pitch.",
  },
  {
    index: "02",
    title: "Understanding",
    body:
      "We spend time looking at the specifics — rounds, routes, numbers, software, customer base. We ask the questions you'd want someone to ask.",
  },
  {
    index: "03",
    title: "Practical plan",
    body:
      "A plain-English plan: what we'd recommend, in what order, and why. If something isn't worth doing, we'll say so.",
  },
  {
    index: "04",
    title: "Implementation",
    body:
      "We put the plan into practice — alongside you, remotely or on-site as appropriate. No endless strategy decks, just getting it done.",
  },
  {
    index: "05",
    title: "Ongoing support",
    body:
      "Dairies evolve, markets shift, people move on. We stay close, review how it's going, and adjust where needed.",
  },
];

export default function Approach() {
  return (
    <SiteLayout>
      <SEO
        title="Our Approach — Fourbees Consultancy"
        description="A simple, honest consultancy process: conversation, understanding, practical plan, implementation, ongoing support. No unnecessary jargon or inflated reports."
        path="/approach"
      />

      {/* Hero */}
      <section className="bg-cream py-24 md:py-32" data-testid="approach-hero">
        <div className="container-site">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <div className="section-number mb-3">Our approach</div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-extrabold text-navy leading-[1.0] tracking-[-0.025em]">
                No decks. <br />
                No jargon. <br />
                <span className="text-gold">Just useful work.</span>
              </h1>
            </div>
            <div className="lg:col-span-4">
              <p className="font-body text-base md:text-lg text-charcoal/75 leading-relaxed">
                We don't believe in unnecessary consultancy jargon, complicated reports or
                selling services businesses don't need. We believe in understanding the problem,
                agreeing on realistic solutions and helping businesses put those solutions into
                practice.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="bg-cream border-t border-light-grey" data-testid="approach-steps">
        <div className="container-site">
          {STEPS.map((s, i) => (
            <div
              key={i}
              className={`grid grid-cols-1 lg:grid-cols-12 gap-10 py-16 md:py-20 ${
                i < STEPS.length - 1 ? "border-b border-light-grey" : ""
              }`}
              data-testid={`approach-step-${i}`}
            >
              <div className="lg:col-span-5">
                <div className="font-heading text-7xl md:text-8xl font-extrabold text-gold leading-none">
                  {s.index}
                </div>
              </div>
              <div className="lg:col-span-7 lg:pt-4">
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-navy leading-tight tracking-tight mb-4">
                  {s.title}
                </h2>
                <p className="font-body text-base md:text-lg text-charcoal/80 leading-relaxed max-w-2xl">
                  {s.body}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Closer */}
      <section className="bg-navy text-cream py-20" data-testid="approach-cta">
        <div className="container-site text-center max-w-3xl mx-auto">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-cream leading-tight tracking-tight mb-5">
            Ready to start with step one?
          </h2>
          <p className="font-body text-cream/70 leading-relaxed mb-8">
            One honest conversation is a good place to start.
          </p>
          <Link to="/contact" className="btn-gold" data-testid="approach-cta-btn">
            Start a Conversation
            <ArrowRight size={16} strokeWidth={2.5} />
          </Link>
        </div>
      </section>
    </SiteLayout>
  );
}
